/**
 * Organization-wide billing (all workspaces under payer org_id) — GET /orgs/:orgId/usage + infra-usage.
 * RBAC: OWNER + JWT custom:org_id must match URL org id.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { Link } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { motion } from 'framer-motion';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import { canAccessOrgBilling } from '@/utils/stepflowRoles';
import PlatformBillingPanel, { StorageGaugeDailyBadge } from '@stepflow/ui-workbench/components/PlatformBillingPanel.jsx';
import BudgetAllocationPanel from '@stepflow/ui-shared/components/BudgetAllocationPanel.jsx';
import Spinner from '@stepflow/ui-shared/components/Spinner.jsx';
import { formatCompactNumber } from '@stepflow/ui-shared/utils/formatCompactNumber.js';
import {
  buildAccountBillingSummary,
  AccountBillingSummaryCard,
} from './AccountUsagePage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

// Compact "K" formatting (1,245,238 → "1,245K") — shared house rule.
function formatInt(n) {
  return formatCompactNumber(n);
}

function formatUsd(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const v = Number(n);
  if (v < 0.01 && v > 0) return `<$0.01`;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
}

function TenantUsageTable({ rows }) {
  if (!rows?.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500">
        No tenant-scoped LLM rows in this period (legacy USER# metering may appear under “Legacy”).
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Workspace (tenant)</th>
            <th className="px-4 py-3 text-right">Input</th>
            <th className="px-4 py-3 text-right">Output</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3 text-right">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-slate-50/60">
              <td className="px-4 py-3 font-medium text-gray-900">
                {row.tenantId == null ? (
                  <span className="text-gray-500">Legacy / unscoped</span>
                ) : (
                  <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{row.tenantId}</code>
                )}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.inputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.outputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                {formatInt(row.totalTokens ?? (row.inputTokens || 0) + (row.outputTokens || 0))}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                {formatUsd(row.estimatedCost)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrgModelTable({ rows }) {
  if (!rows?.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500">
        No model usage in this period yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Model</th>
            <th className="px-4 py-3 text-right">Input</th>
            <th className="px-4 py-3 text-right">Output</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3 text-right">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-slate-50/60">
              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                {row.modelLabel || '—'}
              </td>
              <td className="px-4 py-3">
                <div
                  className="font-medium text-gray-900 truncate max-w-md"
                  title={String(row.modelId || row.modelLabel || '')}
                >
                  {row.modelId || row.modelLabel || '—'}
                </div>
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.inputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.outputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                {formatInt(row.totalTokens ?? (row.inputTokens || 0) + (row.outputTokens || 0))}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                {formatUsd(row.estimatedCost)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrgTenantPoolTable({ rows }) {
  if (!rows?.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500">
        No workspace + pool rows in this period.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Workspace (tenant)</th>
            <th className="px-4 py-3">Customer pool</th>
            <th className="px-4 py-3 text-right">Input</th>
            <th className="px-4 py-3 text-right">Output</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3 text-right">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-slate-50/60">
              <td className="px-4 py-3 font-medium text-gray-900">
                {row.tenantId == null ? (
                  <span className="text-gray-500">Legacy / unscoped</span>
                ) : (
                  <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{row.tenantId}</code>
                )}
              </td>
              <td className="px-4 py-3">
                <code className="text-xs bg-teal-50 text-teal-900 px-1.5 py-0.5 rounded">{row.poolSlug}</code>
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.inputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.outputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                {formatInt(row.totalTokens ?? (row.inputTokens || 0) + (row.outputTokens || 0))}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                {formatUsd(row.estimatedCost)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrgPoolSummaryTable({ rows }) {
  if (!rows?.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500">
        No pool-attributed org usage in this period.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Customer pool</th>
            <th className="px-4 py-3 text-right">Input</th>
            <th className="px-4 py-3 text-right">Output</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3 text-right">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-slate-50/60">
              <td className="px-4 py-3 font-medium text-gray-900">
                <code className="text-xs bg-teal-50 text-teal-900 px-1.5 py-0.5 rounded">{row.poolSlug}</code>
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.inputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.outputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                {formatInt(row.totalTokens ?? (row.inputTokens || 0) + (row.outputTokens || 0))}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                {formatUsd(row.estimatedCost)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrgAgentTable({ rows }) {
  if (!rows?.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500">
        No agent-attributed usage in this period yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 py-3">Agent</th>
            <th className="px-4 py-3 text-right">Input</th>
            <th className="px-4 py-3 text-right">Output</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3 text-right">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-50 hover:bg-slate-50/60">
              <td className="px-4 py-3 font-medium text-gray-900">{row.agentId}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.inputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(row.outputTokens)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                {formatInt(row.totalTokens ?? (row.inputTokens || 0) + (row.outputTokens || 0))}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                {formatUsd(row.estimatedCost)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Org-wide billing UI (all tenants under payer org). Used on /usage?tab=org and legacy redirect. */
export function OrgUsagePanel() {
  const { user, workspaceRole } = useAuth();
  const orgId = user?.orgId || '';
  const allowed = canAccessOrgBilling(workspaceRole, orgId);
  const [months, setMonths] = useState(6);
  const [orgLlmScope, setOrgLlmScope] = useState('workspace');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [infraError, setInfraError] = useState(null);
  const [data, setData] = useState(null);
  const [infraData, setInfraData] = useState(null);

  const fetchOrgUsage = useCallback(async () => {
    if (!allowed || !orgId) return;
    setLoading(true);
    setError(null);
    setInfraError(null);
    const idToken = getRestApiBearerToken();
    const q = new URLSearchParams({ months: String(months) });
    const enc = encodeURIComponent(orgId);
    const headers = {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };
    try {
      const [usageRes, infraRes] = await Promise.all([
        fetch(`${API_BASE_URL}/orgs/${enc}/usage?${q}`, { headers }),
        fetch(`${API_BASE_URL}/orgs/${enc}/infra-usage?${q}`, { headers }),
      ]);
      const usageBody = await usageRes.json().catch(() => ({}));
      const infraBody = await infraRes.json().catch(() => ({}));
      if (!usageRes.ok) {
        throw new Error(usageBody.error || usageBody.message || usageRes.statusText);
      }
      setData(usageBody);
      if (!infraRes.ok) {
        setInfraData(null);
        setInfraError(infraBody.error || infraBody.message || infraRes.statusText);
      } else {
        setInfraData(infraBody);
        setInfraError(null);
      }
    } catch (e) {
      setError(e.message || 'Failed to load organization usage');
      setData(null);
      setInfraData(null);
    } finally {
      setLoading(false);
    }
  }, [months, orgId, allowed]);

  useEffect(() => {
    if (!allowed) return;
    fetchOrgUsage();
  }, [fetchOrgUsage, allowed]);

  const totals = data?.totals;
  const totalsPoolOnly = data?.totalsPoolOnly;
  const range = data?.range;
  const byModel = data?.byModel || [];
  const byAgent = data?.byAgent || [];
  const byTenant = data?.byTenant || [];
  const byPool = data?.byPool || [];
  const byTenantPool = data?.byTenantPool || [];
  const monthly = data?.monthly || [];
  const monthlyPool = data?.monthlyPool || [];

  const orgLlmTotals = useMemo(() => {
    if (orgLlmScope !== 'byPool') return totals;
    if (totalsPoolOnly && typeof totalsPoolOnly.estimatedCost === 'number') return totalsPoolOnly;
    const tin = byPool.reduce((s, r) => s + (Number(r.inputTokens) || 0), 0);
    const tout = byPool.reduce((s, r) => s + (Number(r.outputTokens) || 0), 0);
    const cost = byPool.reduce((s, r) => s + (Number(r.estimatedCost) || 0), 0);
    return {
      inputTokens: tin,
      outputTokens: tout,
      totalTokens: tin + tout,
      estimatedCost: cost,
    };
  }, [orgLlmScope, totals, totalsPoolOnly, byPool]);

  const orgLlmMonthly = orgLlmScope === 'byPool' ? monthlyPool : monthly;

  const maxMonthCost = useMemo(() => {
    let m = 0;
    orgLlmMonthly.forEach((row) => {
      const c = Number(row.estimatedCost) || 0;
      if (c > m) m = c;
    });
    return m || 1;
  }, [orgLlmMonthly]);

  const billingSummary = useMemo(
    () => buildAccountBillingSummary(data, infraData),
    [data, infraData]
  );

  const privilegedOrg = ['OWNER', 'ADMIN'].includes(workspaceRole ?? '');
  if (privilegedOrg && !orgId) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center bg-slate-50 px-6 py-16">
        <p className="text-center text-gray-600 max-w-md">
          Organization billing needs a payer org id on your session. Switch workspace or sign in again so{' '}
          <code className="text-xs bg-white px-1 rounded">POST /auth/session</code> can attach{' '}
          <code className="text-xs bg-white px-1 rounded">orgId</code> / token <code className="text-xs bg-white px-1 rounded">oid</code>{' '}
          from workspace meta (or Cognito <code className="text-xs bg-white px-1 rounded">custom:org_id</code>).
        </p>
        <Link to="/usage" className="mt-6 text-sm font-medium text-blue-600 hover:text-blue-700">
          Workspace account billing &amp; usage
        </Link>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center bg-slate-50 px-6 py-16">
        <p className="text-center text-gray-600">
          Organization billing is only available to workspace owners and admins with a configured payer org id.
        </p>
        <Link to="/usage" className="mt-6 text-sm font-medium text-blue-600 hover:text-blue-700">
          Workspace billing &amp; usage
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-gradient-to-b from-slate-50 to-white">
      <div className="mx-auto max-w-6xl px-6 py-8 pb-12">
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-gray-900">Organization billing</h1>
              <p className="mt-1 text-sm text-gray-500">
                All workspaces under payer org <code className="text-xs bg-slate-100 px-1 rounded">{orgId}</code>
                {range?.from && range?.to ? (
                  <span className="text-gray-400">
                    {' '}
                    · {range.from} → {range.to}
                  </span>
                ) : null}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label
                htmlFor="org-usage-months"
                className="text-xs font-medium uppercase tracking-wide text-gray-500"
              >
                Range
              </label>
              <select
                id="org-usage-months"
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30"
              >
                <option value={3}>Last 3 months</option>
                <option value={6}>Last 6 months</option>
                <option value={12}>Last 12 months</option>
              </select>
            </div>
          </div>
        </motion.div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}

        {loading && !data ? (
          <Spinner variant="panel" size="lg" label="Loading usage…" />
        ) : (
          <Tab.Group>
            <Tab.List className="mb-6 flex flex-wrap w-fit gap-1 rounded-lg bg-slate-200/70 p-1">
              {[
                { label: 'Billing', title: 'Org-wide AI + platform line items' },
                { label: 'Tenants', title: 'Allocate the org points budget across worker tenants' },
                { label: 'LLM tokens', title: 'Aggregated LLM usage across all tenant workspaces' },
                { label: 'Platform', title: 'Infra + storage across all networks under this org' },
              ].map(({ label, title }) => (
                <Tab
                  key={label}
                  title={title}
                  className={({ selected }) =>
                    `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  {label}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              <Tab.Panel>
                <AccountBillingSummaryCard
                  summary={billingSummary}
                  range={range}
                  infraError={infraError}
                  title="Organization billing summary"
                  subtitle="Roll-up of every workspace whose metering rows carry this org’s payer key (OrgBillingIndex). Workspace-level views remain for operational detail."
                />
              </Tab.Panel>

              <Tab.Panel>
                {/* Master tenant view: allocate the org's points budget across
                    its worker tenants (USAGE-AND-BUDGET.md §7b). */}
                <BudgetAllocationPanel
                  scope="org"
                  id={orgId}
                  childNoun="tenant"
                  title="Budget by tenant"
                />
              </Tab.Panel>

              <Tab.Panel>
                <>
                  <div className="mb-4 flex flex-wrap gap-1 rounded-lg bg-slate-200/70 p-1 w-fit">
                    {[
                      { id: 'workspace', label: 'Workspace usage' },
                      { id: 'byPool', label: 'By customer pool' },
                    ].map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setOrgLlmScope(id)}
                        className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                          orgLlmScope === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="mb-4 text-xs text-gray-500 max-w-2xl">
                    {orgLlmScope === 'byPool'
                      ? 'Customer pool attribution (#POOL# metering rows only). Rolls up payer org → workspace (tenant) → pool slug.'
                      : 'Workspace-level LLM totals (#POOL# duplicate rows excluded). Aligns with owner billing without double-counting pool rollups.'}
                  </p>
                  <h2 className="mb-6 text-base font-semibold text-gray-900">
                    {orgLlmScope === 'byPool' ? 'Usage by customer pool' : 'LLM tokens & cost'}
                  </h2>
                  <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {[
                      { label: 'Est. cost', value: formatUsd(orgLlmTotals?.estimatedCost), sub: orgLlmScope === 'byPool' ? 'Pool rows only' : 'USD (approx.)' },
                      { label: 'Total tokens', value: formatInt(orgLlmTotals?.totalTokens), sub: 'input + output' },
                      { label: 'Input tokens', value: formatInt(orgLlmTotals?.inputTokens), sub: 'prompt' },
                      { label: 'Output tokens', value: formatInt(orgLlmTotals?.outputTokens), sub: 'completion' },
                    ].map((k) => (
                      <div
                        key={k.label}
                        className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                      >
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{k.label}</p>
                        <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{k.value}</p>
                        <p className="mt-0.5 text-xs text-gray-400">{k.sub}</p>
                      </div>
                    ))}
                  </div>

                  {orgLlmMonthly.length > 0 && (
                    <div className="mb-10 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                      <h2 className="mb-4 text-sm font-semibold text-gray-800">
                        {orgLlmScope === 'byPool' ? 'Pool-attributed cost by month' : 'Cost by month'}
                      </h2>
                      <div className="flex h-40 items-end gap-1 pl-1 sm:gap-2">
                        {orgLlmMonthly.map((row) => {
                          const cost = Number(row.estimatedCost) || 0;
                          const h = maxMonthCost > 0 ? Math.max(8, (cost / maxMonthCost) * 100) : 8;
                          return (
                            <div
                              key={row.month}
                              className="group flex min-w-0 flex-1 h-full flex-col items-center justify-end"
                            >
                              <div
                                className={`mx-auto w-full max-w-[48px] rounded-t-md opacity-90 transition-opacity group-hover:opacity-100 ${
                                  orgLlmScope === 'byPool'
                                    ? 'bg-gradient-to-t from-teal-600 to-teal-400'
                                    : 'bg-gradient-to-t from-violet-600 to-violet-400'
                                }`}
                                style={{ height: `${h}%` }}
                                title={`${row.month}: ${formatUsd(cost)}`}
                              />
                              <span className="mt-2 w-full truncate text-center text-[10px] text-gray-500 sm:text-xs">
                                {row.month.slice(5)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {orgLlmScope === 'byPool' ? (
                    <Tab.Group>
                      <Tab.List className="mb-4 flex w-fit flex-wrap gap-1 rounded-lg bg-gray-100/80 p-1">
                        {['By pool', 'By workspace + pool'].map((label) => (
                          <Tab
                            key={label}
                            className={({ selected }) =>
                              `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                                selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                              }`
                            }
                          >
                            {label}
                          </Tab>
                        ))}
                      </Tab.List>
                      <Tab.Panels>
                        <Tab.Panel>
                          <OrgPoolSummaryTable rows={byPool} />
                        </Tab.Panel>
                        <Tab.Panel>
                          <OrgTenantPoolTable rows={byTenantPool} />
                        </Tab.Panel>
                      </Tab.Panels>
                    </Tab.Group>
                  ) : (
                    <Tab.Group>
                      <Tab.List className="mb-4 flex w-fit flex-wrap gap-1 rounded-lg bg-gray-100/80 p-1">
                        {['By workspace', 'By model', 'By agent'].map((label) => (
                          <Tab
                            key={label}
                            className={({ selected }) =>
                              `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                                selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                              }`
                            }
                          >
                            {label}
                          </Tab>
                        ))}
                      </Tab.List>
                      <Tab.Panels>
                        <Tab.Panel>
                          <TenantUsageTable rows={byTenant} />
                        </Tab.Panel>
                        <Tab.Panel>
                          <OrgModelTable rows={byModel} />
                        </Tab.Panel>
                        <Tab.Panel>
                          <OrgAgentTable rows={byAgent} />
                        </Tab.Panel>
                      </Tab.Panels>
                    </Tab.Group>
                  )}
                </>
              </Tab.Panel>

              <Tab.Panel>
                <div className="flex flex-wrap items-center gap-2 gap-y-1 mb-2">
                  <h2 className="text-base font-semibold text-gray-900">Platform &amp; storage (organization)</h2>
                  <StorageGaugeDailyBadge />
                </div>
                <p className="text-xs text-gray-500 mb-4 max-w-3xl">
                  Aggregated from metering rows on your org payer key. Historical data may be incomplete until all writers
                  set <code className="text-xs">org_id</code>.
                </p>
                <PlatformBillingPanel
                  orgMode
                  orgId={orgId}
                  months={months}
                  onMonthsChange={setMonths}
                  suppressCustomerEstimates
                />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        )}
      </div>
    </div>
  );
}
